import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useNavigate } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import { colors, getAccountChartColor } from "../../lib/colors"
import StatCard from "../../components/analytics/StatCard"
import LineChart from "../../components/analytics/LineChart"
import DonutChart from "../../components/analytics/DonutChart"
import PostGrid from "../../components/analytics/PostGrid"
import AudienceStats from "../../components/analytics/AudienceStats"
import PeriodSelectorModal from "../../components/analytics/PeriodSelectorModal"
import DashboardReportPanel from "../../components/analytics/DashboardReportPanel"
import { readStoredPeriods, savePeriods, presetToRange } from "../../lib/dateRange"
import type { PeriodEntry } from "../../lib/dateRange"
import { api } from "../../lib/api"
import type { DashboardData, ChartData, PostThumbnail, TrendsData } from "../../types/analytics"
import { useSelectedAccount } from "../../contexts/AccountContext"
import AccountSelectionNotice from "../../components/account/AccountSelectionNotice"
import FreeWindowNotice from "../../components/account/FreeWindowNotice"
import { useEarliestAccountDate } from "../../hooks/useEarliestAccountDate"
import { PAGE_TITLES } from "../../lib/navItems"
import { downloadCsv, downloadChartAsPng, chartDataToCsvRows, todayStr } from "../../lib/csv"
import "../../styles/analytics.scss"

const STORAGE_KEY      = "lyto.dashboardPeriod"
const TREND_METRIC_KEY = "lyto.dashboardTrendMetric"

const readStoredTrendMetric = (): TrendMetric => {
  try {
    const v = sessionStorage.getItem(TREND_METRIC_KEY)
    if (v === "followers" || v === "profile_visits" || v === "reach_by_type") return v
  } catch {}
  return "followers"
}

type TrendMetric = "followers" | "profile_visits" | "reach_by_type"

const TREND_TABS: { key: TrendMetric; label: string }[] = [
  { key: "followers",      label: "フォロワー数" },
  { key: "profile_visits", label: "プロフィールアクセス" },
  { key: "reach_by_type",  label: "フォロワー/非フォロワーリーチ" },
]

const fmtDateRange = (p: PeriodEntry) =>
  `${p.startDate.replace(/-/g, "/")} 〜 ${p.endDate.replace(/-/g, "/")}`

const buildFollowerTooltip = (startDate: string, endDate: string): string => {
  const periodDays = Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000) + 1
  const prevEnd    = new Date(startDate)
  prevEnd.setDate(prevEnd.getDate() - 1)
  const prevStart  = new Date(prevEnd)
  prevStart.setDate(prevStart.getDate() - (periodDays - 1))
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  return `${endDate} 時点のフォロワー数\n▲▼は前の同期間（${fmt(prevStart)}〜${fmt(prevEnd)}）末日時点のフォロワー数との比較`
}

const applyAccountColors = (datasets: TrendsData["followers"], labels: string[]): ChartData => ({
  labels,
  datasets: datasets.map((ds, i) => {
    const c = getAccountChartColor(i)
    return { ...ds, borderColor: ds.borderColor ?? c.border, backgroundColor: ds.backgroundColor ?? c.bg }
  }),
})

const buildReachByTypeChartData = (trends: TrendsData): ChartData => {
  const followerDatasets = trends.follower_reach.map((ds, i) => {
    const c = getAccountChartColor(i)
    return { ...ds, label: `${ds.label} (フォロワー)`, borderColor: c.border, backgroundColor: c.bg }
  })
  const nonFollowerDatasets = trends.non_follower_reach.map((ds, i) => {
    const c = getAccountChartColor(i)
    return { ...ds, label: `${ds.label} (非フォロワー)`, borderColor: c.border, backgroundColor: c.bg, borderDash: [5, 5] }
  })
  return { labels: trends.labels, datasets: [...followerDatasets, ...nonFollowerDatasets] }
}

const buildTrendChartData = (metric: TrendMetric, trends: TrendsData): ChartData => {
  if (metric === "reach_by_type") return buildReachByTypeChartData(trends)
  return applyAccountColors(trends[metric], trends.labels)
}

const DashboardPage = () => {
  const navigate = useNavigate()
  const { selectedAccountIds, organizations, setSingleAccountOnly, currentPermission } = useSelectedAccount()
  const earliestDate = useEarliestAccountDate()
  const canEdit                  = currentPermission === "edit"
  const trendChartRef            = useRef<HTMLDivElement>(null)
  const engagementBreakdownRef   = useRef<HTMLDivElement>(null)

  const freeWindowAccount = selectedAccountIds
    .map(id => organizations.flatMap(o => o.accounts).find(a => a.id === id))
    .find(a => a?.plan === "free")

  const singleAccountId = selectedAccountIds.length === 1 ? selectedAccountIds[0] : null
  const singleAccount   = singleAccountId
    ? organizations.flatMap(o => o.accounts).find(a => a.id === singleAccountId)
    : null

  const handleViewSourcePeriod = useCallback((month: string) => {
    const start = month
    const d = new Date(month)
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10)
    const next: PeriodEntry[] = [{ preset: "custom", startDate: start, endDate: end }]
    setPeriods(next)
    savePeriods(STORAGE_KEY, next)
  }, [])

  const accountColorMap = useMemo(() => {
    const map: Record<string, string> = {}
    selectedAccountIds.forEach((id, i) => {
      const account = organizations.flatMap(o => o.accounts).find(a => a.id === id)
      if (account) map[account.username] = getAccountChartColor(i).border
    })
    return map
  }, [selectedAccountIds, organizations])

  const applyPostColors = (posts: PostThumbnail[] | null | undefined) =>
    posts?.map(p => ({ ...p, color: p.username ? accountColorMap[p.username] : undefined })) ?? null

  const [dataByPeriod, setDataByPeriod] = useState<(DashboardData | null)[]>([null])
  const [loading, setLoading]           = useState(true)
  const [periods, setPeriods]           = useState<PeriodEntry[]>(() => readStoredPeriods(STORAGE_KEY))
  const [trendMetric, setTrendMetric]   = useState<TrendMetric>(readStoredTrendMetric)

  useEffect(() => {
    if (!earliestDate) return
    setPeriods(prev => {
      if (prev[0]?.preset !== "all") return prev
      return [{ ...prev[0], ...presetToRange("all", earliestDate) }, ...prev.slice(1)]
    })
  }, [earliestDate])

  useEffect(() => {
    setSingleAccountOnly(periods.length > 1)
  }, [periods, setSingleAccountOnly])

  const fetchData = useCallback(async () => {
    if (!periods[0]?.startDate || !periods[0]?.endDate) { setLoading(false); return }
    if (selectedAccountIds.length === 0) { setLoading(false); return }
    setLoading(true)
    try {
      const results = await Promise.all(
        periods.map(p => {
          const params = new URLSearchParams({ start_date: p.startDate, end_date: p.endDate })
          selectedAccountIds.forEach(id => params.append("instagram_account_ids[]", String(id)))
          return api.get<DashboardData>(`/analytics/dashboard?${params}`).catch(() => null)
        })
      )
      setDataByPeriod(results)
    } catch {
    } finally {
      setLoading(false)
    }
  }, [selectedAccountIds, periods])

  useEffect(() => { fetchData() }, [fetchData])

  const data0 = dataByPeriod[0]
  const p0    = periods[0]

  const combinedTrendChart = useMemo(() => {
    const p0Data = dataByPeriod[0]
    if (!p0Data?.trends?.labels?.length) return { labels: [] as string[], datasets: [] }
    if (dataByPeriod.length <= 1) return buildTrendChartData(trendMetric, p0Data.trends)

    const p0Chart = buildTrendChartData(trendMetric, p0Data.trends)
    return {
      labels: p0Chart.labels,
      datasets: dataByPeriod.flatMap((data, pi) => {
        if (!data?.trends?.labels?.length || pi >= periods.length) return []
        const chart = buildTrendChartData(trendMetric, data.trends)
        const prefix = `${fmtDateRange(periods[pi])} | `
        return chart.datasets.map((ds, di) => {
          const c = getAccountChartColor(pi * p0Chart.datasets.length + di)
          return { ...ds, label: `${prefix}${ds.label}`, borderColor: c.border, backgroundColor: c.bg }
        })
      }),
    }
  }, [dataByPeriod, periods, trendMetric])

  return (
    <AppLayout pageTitle={PAGE_TITLES["/"]}>
      <div className="analytics">
        {freeWindowAccount && <FreeWindowNotice days={freeWindowAccount.features.free_window_days} />}
        <div className="analytics__header">
          <PeriodSelectorModal
            periods={periods}
            onChange={setPeriods}
            storageKey={STORAGE_KEY}
            allTimeStart={earliestDate}
            maxDays={freeWindowAccount?.features.free_window_days}
          />
        </div>

        {selectedAccountIds.length === 0 ? (
          <AccountSelectionNotice />
        ) : loading ? (
          <p style={{ color: colors.gray400 }}>読み込み中…</p>
        ) : !data0 ? (
          <p style={{ color: colors.gray400 }}>データを取得できませんでした</p>
        ) : (
          <>
            {/* StatCards - メイン期間のみ */}
            <div className="analytics__stats-grid">
              <StatCard label="フォロワー数"        stat={data0.stats.followers}          tooltip={buildFollowerTooltip(p0.startDate, p0.endDate)} />
              <StatCard label="リーチ"              stat={data0.stats.reach} />
              <StatCard label="インタラクション"   stat={data0.stats.total_interactions} />
              <StatCard label="プロフィールアクセス" stat={data0.stats.profile_visits_total} />
            </div>

            {/* 2カラムレイアウト: 左=トレンド+エンゲージメント / 右=ダッシュボードレポートパネル */}
            <div className="analytics__report-layout">
              <div className="analytics__report-layout-charts">
                {/* トレンドグラフ - 全期間を1チャートに統合 */}
                {combinedTrendChart.labels.length > 0 && (
                  <div className="chart-card">
                    <div className="chart-card__header chart-card__header--with-tabs">
                      <div className="chart-card__tabs">
                        {TREND_TABS.map(({ key, label }) => (
                          <button
                            key={key}
                            className={`chart-card__tab${trendMetric === key ? " chart-card__tab--active" : ""}`}
                            onClick={() => {
                              setTrendMetric(key)
                              try { sessionStorage.setItem(TREND_METRIC_KEY, key) } catch {}
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <div className="chart-card__actions">
                        <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(combinedTrendChart), `dashboard_trend_${trendMetric}_${todayStr()}.csv`)}>↓ CSV</button>
                        <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(trendChartRef.current, `dashboard_trend_${trendMetric}_${todayStr()}.png`)}>↓ PNG</button>
                      </div>
                    </div>
                    <div className="chart-card__canvas-wrap" ref={trendChartRef}>
                      <LineChart data={combinedTrendChart} beginAtZero dateAxis />
                    </div>
                  </div>
                )}

                {/* エンゲージメント内訳 - メイン期間のみ */}
                <div className="chart-card">
                  <div className="chart-card__header">
                    <span className="chart-card__title">エンゲージメント内訳</span>
                    {data0.engagement_breakdown && (
                      <div className="chart-card__actions">
                        <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(data0.engagement_breakdown!), `engagement_breakdown_${todayStr()}.csv`)}>↓ CSV</button>
                        <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(engagementBreakdownRef.current, `engagement_breakdown_${todayStr()}.png`)}>↓ PNG</button>
                      </div>
                    )}
                  </div>
                  <div className="chart-card__canvas-wrap" ref={engagementBreakdownRef}>
                    {data0.engagement_breakdown
                      ? <DonutChart data={data0.engagement_breakdown} />
                      : <p className="chart-card__no-data">データなし</p>
                    }
                  </div>
                </div>
              </div>

              {/* ダッシュボードレポートパネル */}
              {singleAccountId && singleAccount ? (
                <DashboardReportPanel
                  accountId={singleAccountId}
                  accountCreatedAt={singleAccount.created_at}
                  canEdit={canEdit}
                  locked={!singleAccount.features.ai}
                  pptxEnabled={singleAccount.features.pptx_export}
                  onViewSourcePeriod={handleViewSourcePeriod}
                />
              ) : (
                <div className="dashboard-report">
                  <div className="dashboard-report__header">
                    <p className="ai-report__muted">
                      {selectedAccountIds.length === 0
                        ? "アカウントを選択してください"
                        : "ダッシュボードレポートは1つのアカウントを選択している時のみ生成できます。"}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* オーディエンス統計 - メイン期間のみ */}
            <div className="analytics__section">
              <AudienceStats
                initialData={data0.audience_stats}
                accountIds={selectedAccountIds}
                startDate={p0.startDate}
                endDate={p0.endDate}
              />
            </div>

            {/* TOP5 セクション - メイン期間のみ */}
            <div className="analytics__top-grid">
              {[
                { title: "フィードTOP5",    posts: data0.top_feed,    path: "/feed-posts" },
                { title: "ストーリーズTOP5", posts: data0.top_stories, path: "/stories" },
                { title: "リールTOP5",      posts: data0.top_reels,   path: "/reels" },
              ].map(({ title, posts, path }) => (
                <div key={title} className="top-posts">
                  <div className="top-posts__header">
                    <span className="top-posts__title">{title}</span>
                  </div>
                  {!posts || posts.length === 0
                    ? <div className="top-posts__empty">データ収集中です</div>
                    : <PostGrid
                        posts={applyPostColors(posts)}
                        aspect="square"
                        onPostClick={(postId) => navigate(`${path}/${postId}`)}
                      />
                  }
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default DashboardPage
